import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Container from '@mui/material/Container';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import { AuthProvider, useAuth } from './context/AuthContext';
import ApiList from './components/ApiList';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import PostLoginReturnTo from './components/PostLoginReturnTo';
import LoycoBrandLink, { LOYCO_BRAND_BG } from './components/LoycoBrandLink';

const ApiDetail = lazy(() => import('./components/ApiDetail'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const GetMyApiKey = lazy(() => import('./components/GetMyApiKey'));
const Diagnostics = lazy(() => import('./components/Diagnostics'));
const SimpleTest = lazy(() => import('./components/SimpleTest'));

function RouteFallback() {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
      <CircularProgress />
    </Box>
  );
}

/** Old SPA path /api/:id was proxied to the backend on refresh — redirect to /apis/:id. */
function LegacyApiDetailRedirect() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <Navigate to="/" replace />;
  return <Navigate to={`/apis/${id}`} replace />;
}

// Loyco brand colors based on https://www.loyco.io
const theme = createTheme({
  palette: {
    primary: {
      main: '#113d51',
      light: '#1a5a73',
      dark: '#0c2d3b',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#00A8E8', // Accent blue
      light: '#33B9ED',
      dark: '#0085B5',
      contrastText: '#ffffff',
    },
    background: {
      default: '#F8F9FA',
      paper: '#ffffff',
    },
    text: {
      primary: '#1A1A1A',
      secondary: '#666666',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.4,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.5,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontWeight: 500,
        },
        contained: {
          boxShadow: '0 2px 8px rgba(0, 102, 204, 0.2)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 102, 204, 0.3)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          borderRadius: 12,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        },
      },
    },
  },
});

function AppBarContent() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
  };

  return (
    <AppBar position="static" sx={{ backgroundColor: '#ffffff', color: '#1A1A1A', borderBottom: '1px solid #E0E0E0' }}>
      <Toolbar sx={{ minHeight: '70px !important' }}>
        <LoycoBrandLink to="/" logoHeight={36} sx={{ mr: 3 }} />
        <Typography
          variant="body1"
          sx={{
            flexGrow: 1,
            color: '#666666',
            fontWeight: 500,
            display: { xs: 'none', md: 'block' },
          }}
        >
          API Portal
        </Typography>
        {user ? (
          <>
            <Button 
              component={Link}
              to="/"
              sx={{ 
                ml: 1,
                color: '#666666',
                '&:hover': {
                  backgroundColor: 'rgba(17, 61, 81, 0.08)',
                  color: LOYCO_BRAND_BG,
                }
              }}
            >
              Home
            </Button>
            {user.isAdmin && (
              <Button 
                component={Link}
                to="/admin"
                sx={{ 
                  ml: 1,
                  color: '#666666',
                  '&:hover': {
                    backgroundColor: 'rgba(17, 61, 81, 0.08)',
                    color: LOYCO_BRAND_BG,
                  }
                }}
              >
                Admin
              </Button>
            )}
            <Box sx={{ ml: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">{user.name || user.email}</Typography>
              <IconButton
                size="small"
                onClick={handleMenuOpen}
                sx={{ ml: 1 }}
              >
                <Avatar 
                  src={user.pictureUrl} 
                  sx={{ width: 32, height: 32 }}
                >
                  {user.name?.charAt(0) || user.email?.charAt(0)?.toUpperCase() || '?'}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={handleLogout}>Logout</MenuItem>
              </Menu>
            </Box>
          </>
        ) : (
          <Button 
            component={Link} 
            to="/login"
            variant="contained"
            sx={{
              backgroundColor: `${LOYCO_BRAND_BG} !important`,
              color: '#ffffff',
              borderRadius: '25px',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#0c2d3b !important',
                boxShadow: 'none',
              },
            }}
          >
            Sign in
          </Button>
        )}
      </Toolbar>
    </AppBar>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <PostLoginReturnTo />
            <AppBarContent />
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                <Route path="/login" element={<Login />} />
                <Route 
                  path="/" 
                  element={
                    <ProtectedRoute>
                      <ApiList />
                    </ProtectedRoute>
                  } 
                />
                {/* SPA path must not be /api/* — nginx proxies /api to backend; refresh would 404. */}
                <Route path="/api/:id" element={<LegacyApiDetailRedirect />} />
                <Route
                  path="/apis/:id"
                  element={
                    <ProtectedRoute>
                      <ApiDetail />
                    </ProtectedRoute>
                  }
                />
                <Route 
                  path="/admin" 
                  element={
                    <ProtectedRoute requireAdmin>
                      <AdminPanel />
                    </ProtectedRoute>
                  } 
                />
                <Route
                  path="/getmyapikey"
                  element={
                    <ProtectedRoute>
                      <GetMyApiKey />
                    </ProtectedRoute>
                  }
                />
                <Route path="/diagnostics" element={<Diagnostics />} />
                <Route path="/test" element={<SimpleTest />} />
              </Routes>
              </Suspense>
            </Container>
          </Box>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
